// Import React and necessary components
import siteMessage from "@insite/client-framework/SiteMessage";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Check from "@insite/mobius/Icons/Check";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    isQtyAdjusted: boolean;
    multipleProducts: boolean;
    productTitle?: string;
    selectedImage?: ImageModel;
    product: ProductModel;
    onButtonClick: () => void;
    unitListPriceDisplay: string;
    unitOfMeasure: string;
    qtyOrdered?: number;
}

type Props = OwnProps;

export interface ProductAddedToCartMessageStyles {
    messageWrapper?: GridContainerProps;
    defaultText?: TypographyPresentationProps;
    quantityAdjustedText?: TypographyPresentationProps;
    productTitleText?: TypographyPresentationProps;
    productTitleGridItem?: GridItemProps;
    productImageGridItem?: GridItemProps;
    imageStyles?: ProductPrimaryImageStyles;
    checkIcon?: IconPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    buttonContainerGridItem?: GridItemProps;
    priceText?: TypographyPresentationProps;
    productPriceGridItem?: GridItemProps;
    productQtyGridItem?: GridItemProps;
    productDetailsGridContainer?: GridContainerProps;
    messageTitleGridItem?: GridItemProps;
}

export const productAddedToCartMessageStyles: ProductAddedToCartMessageStyles = {
    defaultText: {
        css: css`
            font-weight: 700;
            font-size: 22px;
        `,
    },
    quantityAdjustedText: {
        variant: "p",
    },
    productTitleText: {
        css: css`
            font-weight: 700;
            font-size: 18px;
        `,
    },
    productTitleGridItem: {
        width: 12,
    },
    productImageGridItem: {
        width: 12,
        css: css``,
    },
    imageStyles: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 75px;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 75px;
            `,
        },
        centeringWrapper: {
            css: css`
                min-height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
    },
    checkIcon: {
        src: Check,
        color: "primary.main",
        size: 30,
        css: css`
            margin-right: 10px;
            margin-top: 2px;
        `,
    },
    messageWrapper: {
        gap: 10,
        css: css`
            max-height: fit-content;
            display: flex;
            flex-direction: column;
            margin-left: 18px;
        `,
    },
    viewCartButton: {
        shape: "rounded",
        css: css`
            color: ${getColor("secondary")};
            box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            border-radius: 8px;
        `,
    },
    buttonContainerGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
        `,
    },
    priceText: {
        css: css`
            font-weight: 600;
            font-size: 14px;
        `,
    },
    productPriceGridItem: {
        width: 7,
    },
    productQtyGridItem: {
        width: 5,
    },
    productDetailsGridContainer: {
        gap: 5,
        css: css`
            margin-top: 20px;
        `,
    },
    messageTitleGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-start;
        `,
    },
};

const styles = productAddedToCartMessageStyles;

const ProductAddedToCartMessage = ({
    isQtyAdjusted,
    multipleProducts,
    productTitle,
    selectedImage,
    product,
    onButtonClick,
    unitListPriceDisplay,
    qtyOrdered,
}: Props) => {
    const defaultMessage = multipleProducts
        ? siteMessage("Cart_AllProductsAddedToCart")
        : siteMessage("Cart_ProductAddedToCart");

    const addedMessage = "Added to Cart!";

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    if (!isQtyAdjusted) {
        return <Typography {...styles.defaultText}>{defaultMessage}</Typography>;
    }

    return (
        <GridContainer {...styles.messageWrapper}>
            <GridItem {...styles.messageTitleGridItem}>
                <IconMemo {...styles.checkIcon} />
                <Typography {...styles.defaultText}>{addedMessage}</Typography>
            </GridItem>
            <GridItem width={3}>
                <GridItem {...styles.productImageGridItem}>
                    {isQtyAdjusted && (
                        <ProductPrimaryImage product={product} image={image} extendedStyles={styles.imageStyles} />
                    )}
                </GridItem>
            </GridItem>
            <GridItem width={9}>
                <GridContainer {...styles.productDetailsGridContainer}>
                    <GridItem {...styles.productTitleGridItem}>
                        {productTitle && <Typography {...styles.productTitleText}>{productTitle}</Typography>}
                    </GridItem>
                    <GridItem {...styles.productPriceGridItem}>
                        {unitListPriceDisplay && (
                            <Typography {...styles.priceText}>{`Price: ${unitListPriceDisplay}`}</Typography>
                        )}
                    </GridItem>
                    <GridItem {...styles.productQtyGridItem}>
                        <Typography {...styles.priceText}>{`QTY: ${qtyOrdered}`}</Typography>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.buttonContainerGridItem}>
                <Button {...styles.viewCartButton} onClick={onButtonClick}>
                    View Cart & Checkout
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default ProductAddedToCartMessage;
